import { startTransition, useCallback, useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  AUTH_EMAIL_PARAM,
  AUTH_RETURN_TO_PARAM,
  AUTH_SOURCE_PARAM,
  AUTH_VIEW_PARAM,
  SIGN_IN_VIEW,
} from 'common/constants/auth';
import { Alert, Button } from 'components/ui';
import { ACTIVATE_TITLE_LEAD, ACTIVATE_TITLE_TAIL } from 'pages/Auth/ActivateAccount/ActivateAccount.constants';
import {
  AuthFormHeader,
  AuthIdentifierRequestForm,
  AuthPageStack,
  AuthSignInFooterPrompt,
  AuthSuccessPanel,
  AuthTitleText,
} from 'pages/Auth/components';
import {
  useAuthInitialFocus,
  useAuthResendCooldown,
  useAuthSession,
  useAuthViewTransition,
  useClearFieldErrorOnValueChange,
  useResendActivationEmail,
} from 'pages/Auth/hooks';
import {
  buildAuthSearchParams,
  buildSignInUrl,
  clearFieldErrorsByType,
  isEmailLikeIdentifierReady,
  replaceSearchParamsIfChanged,
  sanitizeReturnTo,
} from 'pages/Auth/utils';
import {
  RESEND_ACTIVATION_EMAIL_LABEL,
  RESEND_ACTIVATION_EMAIL_PLACEHOLDER,
  RESEND_ACTIVATION_EMAIL_SENT_DESCRIPTION,
  RESEND_ACTIVATION_EMAIL_SENT_TITLE,
  RESEND_ACTIVATION_ERROR,
  RESEND_ACTIVATION_FORM_SUBMIT_LABEL,
  RESEND_ACTIVATION_FORM_SUBTITLE,
  RESEND_ACTIVATION_RESEND_ACTION_LABEL,
  RESEND_ACTIVATION_RESEND_MISSING_EMAIL_ERROR,
  RESEND_ACTIVATION_VIEW,
  type ResendActivationView,
} from './ResendActivation.constants';
import { RESEND_ACTIVATION_SCHEMA } from './ResendActivation.schema';
import { RESEND_ACTIVATION_TEST_IDS } from './ResendActivation.testIds';
import type { ResendActivationFormValues } from './ResendActivation.types';

const getResendActivationView = (value: string | null): ResendActivationView =>
  value === RESEND_ACTIVATION_VIEW.SENT ? RESEND_ACTIVATION_VIEW.SENT : RESEND_ACTIVATION_VIEW.FORM;

const ResendActivation = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasSession } = useAuthSession();
  const resendActivationRequest = useResendActivationEmail({ useGlobalLoading: false });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isCoolingDown, remainingSeconds, startCooldown } = useAuthResendCooldown();
  const view = getResendActivationView(searchParams.get(AUTH_VIEW_PARAM));
  const email = searchParams.get(AUTH_EMAIL_PARAM) ?? '';
  const source = searchParams.get(AUTH_SOURCE_PARAM);
  const safeReturnTo = sanitizeReturnTo(searchParams.get(AUTH_RETURN_TO_PARAM));
  const { isViewTransitionPending, startViewTransition } = useAuthViewTransition(view);
  const isFormBusy = isSubmitting || isViewTransitionPending;
  const resendActionLabel = isCoolingDown
    ? `${RESEND_ACTIVATION_RESEND_ACTION_LABEL} (${remainingSeconds}s)`
    : RESEND_ACTIVATION_RESEND_ACTION_LABEL;

  const form = useForm<ResendActivationFormValues>({
    defaultValues: {
      identifier: email,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(RESEND_ACTIVATION_SCHEMA),
  });
  const watchedIdentifier = form.watch('identifier');
  const isSubmitDisabled = isFormBusy || !isEmailLikeIdentifierReady(watchedIdentifier);

  const clearIdentifierServerError = useCallback(() => {
    clearFieldErrorsByType(form, ['identifier'], ['server']);
  }, [form]);

  const dismissSubmitError = useCallback(() => {
    setSubmitError(null);
    clearIdentifierServerError();
  }, [clearIdentifierServerError]);

  useEffect(() => {
    form.reset({ identifier: email });
  }, [email, form]);

  // Only the sent state keeps the submitted email in the URL so refresh can still support resend without preserving form-step edits.
  useEffect(() => {
    const shouldStayOnSent = view === RESEND_ACTIVATION_VIEW.SENT && Boolean(email);
    const nextParams = buildAuthSearchParams({
      email: shouldStayOnSent ? email : null,
      returnTo: safeReturnTo,
      source,
      view: shouldStayOnSent ? RESEND_ACTIVATION_VIEW.SENT : RESEND_ACTIVATION_VIEW.FORM,
    });

    replaceSearchParamsIfChanged(searchParams, nextParams, params => {
      setSearchParams(params, { replace: true });
    });
  }, [email, safeReturnTo, searchParams, setSearchParams, source, view]);
  useClearFieldErrorOnValueChange({
    errorTypes: ['server'],
    fieldNames: ['identifier'],
    form,
    onClear: dismissSubmitError,
    shouldClear: () => Boolean(submitError),
    values: [watchedIdentifier],
  });

  const focusEmailField = useCallback(() => {
    form.setFocus('identifier');
  }, [form]);

  useAuthInitialFocus(focusEmailField, view === RESEND_ACTIVATION_VIEW.FORM && !isFormBusy);

  const signInHref = useMemo(
    () =>
      buildSignInUrl({
        returnTo: safeReturnTo,
        source,
        view: SIGN_IN_VIEW.IDENTIFIER,
      }),
    [safeReturnTo, source]
  );

  if (hasSession) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  // Resend-activation only owns the status step in the URL; the editable request form should not preserve typed input across refresh.
  const replaceViewState = (nextView: ResendActivationView, nextEmail?: string | null) => {
    const nextParams = buildAuthSearchParams({
      email: nextView === RESEND_ACTIVATION_VIEW.SENT ? nextEmail ?? null : null,
      returnTo: safeReturnTo,
      source,
      view: nextView,
    });

    startTransition(() => {
      setSearchParams(nextParams, { replace: true });
    });
  };

  const handleSubmit = form.handleSubmit(async values => {
    if (isSubmitting) {
      return;
    }

    const trimmedIdentifier = values.identifier.trim();
    clearIdentifierServerError();
    setSubmitError(null);
    setIsSubmitting(true);

    const [, error] = await resendActivationRequest(trimmedIdentifier);

    if (error) {
      form.setError('identifier', { message: RESEND_ACTIVATION_ERROR, type: 'server' });
      setSubmitError(RESEND_ACTIVATION_ERROR);
      setIsSubmitting(false);
      return;
    }

    startCooldown();
    startViewTransition(RESEND_ACTIVATION_VIEW.SENT);
    replaceViewState(RESEND_ACTIVATION_VIEW.SENT, trimmedIdentifier);
    setIsSubmitting(false);
  });

  const resendActivationEmail = async () => {
    if (isSubmitting) {
      return;
    }

    if (!email) {
      setSubmitError(RESEND_ACTIVATION_RESEND_MISSING_EMAIL_ERROR);
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);

    const [, error] = await resendActivationRequest(email);

    if (error) {
      setSubmitError(RESEND_ACTIVATION_ERROR);
    } else {
      startCooldown();
    }

    setIsSubmitting(false);
  };

  return (
    <AuthPageStack>
      <AuthFormHeader
        subtitle={view === RESEND_ACTIVATION_VIEW.FORM ? RESEND_ACTIVATION_FORM_SUBTITLE : undefined}
        title={<AuthTitleText lead={ACTIVATE_TITLE_LEAD} tail={ACTIVATE_TITLE_TAIL} />}
      />
      {submitError ? (
        <Alert onClose={dismissSubmitError} severity="error" visualVariant="inlineError">
          {submitError}
        </Alert>
      ) : null}
      {view === RESEND_ACTIVATION_VIEW.SENT ? (
        <AuthSuccessPanel
          description={RESEND_ACTIVATION_EMAIL_SENT_DESCRIPTION}
          extraAction={<AuthSignInFooterPrompt disabled={isSubmitting} replace to={signInHref} />}
          mainAction={(
            <Button
              disabled={isCoolingDown}
              loading={isSubmitting}
              loadingBehavior="inline"
              onClick={resendActivationEmail}
              type="button"
              variant="outlined"
              visualVariant="primaryOutline"
            >
              {resendActionLabel}
            </Button>
          )}
          title={RESEND_ACTIVATION_EMAIL_SENT_TITLE}
        />
      ) : (
        <AuthIdentifierRequestForm
          disabled={isFormBusy}
          extraAction={<AuthSignInFooterPrompt disabled={isFormBusy} to={signInHref} />}
          fieldName="identifier"
          form={form}
          hiddenErrorTypes={['server']}
          id="auth-resend-activation-email"
          label={RESEND_ACTIVATION_EMAIL_LABEL}
          onSubmit={handleSubmit}
          placeholder={RESEND_ACTIVATION_EMAIL_PLACEHOLDER}
          submitDisabled={isSubmitDisabled}
          submitLabel={RESEND_ACTIVATION_FORM_SUBMIT_LABEL}
          submitLoading={isFormBusy}
          submitTestId={RESEND_ACTIVATION_TEST_IDS.submitButton}
          submitVisualVariant="positive"
        />
      )}
    </AuthPageStack>
  );
};

export default ResendActivation;
